import { ObjectID } from 'bson';
import mongoose, { Schema } from 'mongoose';
import { arrayMaxLengthValidator } from '../common-validators';
import { MAX_NUMBER_OF_TAGS, Privacy } from '../constants';
import { preSave } from '../hooks/service';
import { getServicePageTitle } from '../methods/page-title';
import accommodationProps, { IAccommodationProps } from './accommodation-props';
import BaseCurrency, { IBaseCurrency } from './baseCurrency';
import category, { ICategory } from './category';
import coupon from './coupon';
import instructions, { IInstruction } from './instructions';
import links, { ILinks } from './links';
import localizedSchema, { ILocalizedText } from './localized-text';
import media, { IMedia } from './media';
import period, { IPeriod } from './period';
import reviewCounts from './review-counts';
import checkoutOptions, { IServiceCheckoutOptions } from './service-checkout-options';
import servicePriceSchema, { ServicePrice } from './service-price';
import servicePricingElement, { IServicePricingElement } from './service-pricing';
import serviceRating, { IServiceRating } from './service-rating';
import serviceLocation, { IServiceLocation } from './serviceLocation';
import smartContract from './smartContract';
import telephoneSchema, { ITelephone } from './telephone';

export interface IService {
  _id: ObjectID;
  source: ObjectID;
  externalId: string;
  privacy: Privacy;
  status: string;
  owner: ObjectID;
  isRealOwner: boolean;
  stars?: number;
  basePrice: ServicePrice;
  pricing: IServicePricingElement[];
  baseCurrency: IBaseCurrency;
  location: IServiceLocation;
  description?: ILocalizedText;
  duration: number;
  externalUrl?: ILocalizedText;
  instructions?: IInstruction;
  rules: ILocalizedText[];
  subtitle?: ILocalizedText;
  title: ILocalizedText;
  links?: ILinks;
  categories: ICategory[];
  media: IMedia[];
  otherAttributes: any;
  periods: IPeriod[];
  ratings: IServiceRating;
  tags: ObjectID[];
  telephones: ITelephone[];
  isAveragePrice: boolean;
  isMostlyAvailable: boolean;
  checkoutOptions?: IServiceCheckoutOptions;
  importerOptions?: {
    fromUrl?: string;
    importPrice?: boolean;
    importRating?: boolean;
  };
  accommodationProps?: IAccommodationProps;
  hearts: number;
  updatedAt: Date;
  createdAt: Date;
}
export interface IServiceDocument extends mongoose.Document, IService {
  _id: ObjectID;
  pricing: mongoose.Types.Array<IServicePricingElement>;
}
const service = new Schema(
  {
    source: {
      type: Schema.Types.ObjectId,
      ref: 'Source',
      required: true,
    },
    externalId: {
      type: String,
      required: true,
    },
    parseUser: {
      // till we have parse in place let's store the parse user id as well
      type: String,
    },
    privacy: {
      type: String,
      required: true,
      default: Privacy.private,
      enum: Object.values(Privacy),
      validate: {
        validator(this: IService, value: string) {
          if (value === 'public' && !(this.media && this.media.length)) {
            throw new Error("Can't make a service public with no images");
          }
          return true;
        },
        // @ts-ignore https://github.com/DefinitelyTyped/DefinitelyTyped/issues/35860
        message: (props: any) => props.reason.message,
      },
    },
    status: {
      type: String,
      default: 'active',
      enum: ['active', 'inactive'],
      required: true,
    },
    owner: {
      type: Schema.Types.ObjectId,
      ref: 'User',
      required: true,
    },
    isRealOwner: {
      type: Boolean,
      required: true,
      default: false,
    },
    stars: {
      type: Number,
      default: undefined,
      required: false,
    },
    basePrice: {
      type: servicePriceSchema,
      required: true,
    },
    baseCurrency: {
      type: BaseCurrency,
      required: true,
    },
    pricing: {
      type: [servicePricingElement],
      required: true,
      default: [],
    },
    location: {
      type: serviceLocation,
      required: true,
    },
    description: localizedSchema,
    duration: {
      type: Number,
      required: true,
      min: [0, 'has to be positive'],
    },
    externalUrl: localizedSchema,
    instructions,
    rules: [localizedSchema],
    subtitle: localizedSchema,
    title: {
      type: localizedSchema,
      required: true,
    },
    links,
    categories: {
      type: [category],
      required: true,
      validate: {
        validator(array: ICategory[]) {
          return array.length >= 1;
        },
      },
    },
    coupons: [coupon],
    media: [media],
    otherAttributes: {},
    periods: {
      type: [period],
      required: true,
    },
    ratings: {
      type: serviceRating,
      default: serviceRating,
    },
    reviewCounts,
    tags: {
      type: [
        {
          type: Schema.Types.ObjectId,
          ref: 'Tag',
          required: true,
        },
      ],
      default: [],
    },
    telephones: {
      type: [
        {
          type: telephoneSchema,
          required: true,
        },
      ],
      default: [],
    },
    smartContract,
    isAveragePrice: {
      type: Boolean,
      default: true,
    },
    originalServicePayload: {
      type: Schema.Types.ObjectId,
      ref: 'OriginalServicePayload',
    },
    isMostlyAvailable: {
      type: Boolean,
      // by default we mark all services available
      // the price-importer is the one who sets it to false
      default: true,
    },
    checkoutOptions: {
      type: checkoutOptions,
      default: checkoutOptions,
    },
    importerOptions: {
      fromUrl: {
        type: String,
      },
      importPrice: {
        type: Boolean,
        default: true,
      },
      importRating: {
        type: Boolean,
        default: true,
      },
    },
    accommodationProps: {
      type: accommodationProps,
      required: false,
    },
    hearts: {
      type: Number,
      default: 0,
    },
  },
  { timestamps: true, toJSON: { virtuals: true }, toObject: { virtuals: true } },
);

service.index({ 'location.geo': '2dsphere' });
service.index({ externalId: 1 }, { unique: true });
service.index({ _id: 1, status: 1, owner: 1 });
service.virtual('pageTitle').get(function(this: IService) {
  return getServicePageTitle(this);
});
service.pre('save', preSave);

export default service;

export type ILocalizedFields = 'title' | 'description' | 'externalUrl' | 'rules' | 'subtitle';
export const localizedFields = [
  'title',
  'description',
  'externalUrl',
  'rules',
  'subtitle',
] as const;
export type IServiceSingleLang<T extends IService> = Omit<T, ILocalizedFields> &
  { [key in ILocalizedFields]?: string };
